import React from 'react'
import { generatePath, matchPath } from 'react-router-dom'
import type { StylesConfig } from 'react-select'
import type { Option } from '@components/Form/Select'
import i18n from 'i18next'
import routes from '@msteams/Routes/routes'
import { filterNavItems } from '@src/components/Navigation/utils'
import type { NavFilterUser } from '@src/components/Navigation/utils'

import { ContactParty } from '@models/ApproveitAccounting'
import type {
  LinkGroupData,
  LinkGroupOption,
  LinkGroupOptionSpec,
  SingleLinkObject,
} from '@msteams/components/Navigation/types'
import { LinkGroupType } from '@msteams/components/Navigation/types'
import { IconSettings } from '@tabler/icons-react'
import { FeaturesPaywall } from '@helpers/paywall'
import { FeatureKey } from '@type/featureKeys'

// Every entry declares the backend feature it belongs to and NOTHING about
// who may see it. The web sidebar's filterNavItems decides that for both
// shells from /my-account's resolved `features` states, so an
// entry registered on the backend and declared here is gated identically
// on web and in Teams — the parity that used to depend on a test.

export const LinkGroupTitles: Record<LinkGroupType, string> = {
  [LinkGroupType.payable]: i18n.t('common:navigation.accountsPayable'),
  [LinkGroupType.receivable]: i18n.t('common:navigation.accountsReceivable'),
}

// The grouped selects show plan-locked options too, locked, pointing at the
// paywall — same upsell surface as the web sidebar.
const INCLUDE_LOCKED = { includeLocked: true }

/**
 * A group's entries as this viewer may see them, through the SAME
 * filterNavItems the web sidebar uses. react-select options carry `value`;
 * the shared filter reads `to`. The resolved entries keep both, which the
 * Option index signature allows.
 */
export const resolveGroupOptions = (group: LinkGroupData, user?: NavFilterUser | null): LinkGroupOption[] =>
  filterNavItems(
    group.options.map((spec) => ({ ...spec, to: spec.value })),
    user ?? undefined,
    undefined,
    INCLUDE_LOCKED,
  )

const PAYABLE_OPTIONS: LinkGroupOptionSpec[] = [
  { feature: FeatureKey.bills, label: i18n.t('common:navigation.bills'), value: routes.private.bills },
  {
    feature: FeatureKey.purchaseOrders,
    label: i18n.t('common:navigation.purchaseOrders'),
    value: routes.private.purchaseOrders,
  },
  { feature: FeatureKey.rfqs, label: i18n.t('common:navigation.rfq'), value: routes.private.rfqs },
  {
    feature: FeatureKey.vendors,
    label: i18n.t('common:navigation.vendors'),
    value: generatePath(routes.private.contacts, { contactParty: ContactParty.vendor }),
  },
]

const RECEIVABLE_OPTIONS: LinkGroupOptionSpec[] = [
  { feature: FeatureKey.invoices, label: i18n.t('common:navigation.invoices'), value: routes.private.invoices },
  { feature: FeatureKey.quotes, label: i18n.t('common:navigation.quotes'), value: routes.private.quotes },
  {
    feature: FeatureKey.costCenters,
    label: i18n.t('common:navigation.costCenters'),
    value: routes.private.costCenter,
  },
  {
    feature: FeatureKey.customers,
    label: i18n.t('common:navigation.customers'),
    value: generatePath(routes.private.contacts, { contactParty: ContactParty.customer }),
  },
]

export const LinkGroups: LinkGroupData[] = [
  {
    options: PAYABLE_OPTIONS,
    isActive: (path: string) => {
      return !![
        routes.private.purchaseOrders,
        routes.private.purchaseOrdersById,
        routes.private.bills,
        routes.private.billById,
        routes.private.contactsList,
        routes.private.contact,
        routes.private.contactContract,
        routes.private.contactContracts,
        routes.private.rfqs,
        routes.private.rfqProposalsById,
        routes.private.rfqById,
        routes.private.costCenter,
        routes.private.costCenterById,
        routes.private.costCenterBudget,
        routes.private.costCenterEditById,
        routes.private.financeSettings,
        routes.private.accountCategories,
        routes.private.financeSettings,
        routes.private.accounts,
        routes.private.accountById,
        routes.private.accountCategories,
        routes.private.accountCategoryById,
        routes.private.companyDetails,
        routes.private.taxes,
        routes.private.taxById,
        routes.private.products,
        routes.private.productById,
        generatePath(routes.private.paywall, { feature: FeaturesPaywall.CostCenters }),
      ].find((route) => matchPath(route, path))
    },
    type: LinkGroupType.payable,
  },
  {
    options: RECEIVABLE_OPTIONS,
    isActive: (path: string) => {
      return !![
        routes.private.invoices,
        routes.private.invoiceById,
        routes.private.quotes,
        routes.private.quoteById,
        routes.private.contactsList,
        routes.private.contact,
        routes.private.contactContract,
        routes.private.costCenter,
        routes.private.costCenterNew,
        routes.private.costCenterById,
        routes.private.costCenterEditById,
        routes.private.costCenterBudget,
        routes.private.costCenterBreakdown,
      ].find((route) => matchPath(route, path))
    },
    type: LinkGroupType.receivable,
  },
]

type SingleLinkSpec = Pick<SingleLinkObject, 'to' | 'label' | 'isActive' | 'feature'>

/**
 * The Teams top nav, in display order (the first four render inline, the
 * rest go to the overflow menu). Personal settings comes first because it
 * is ungated and the landing route of last resort.
 */
export const SINGLE_LINKS: SingleLinkSpec[] = [
  {
    feature: FeatureKey.settings,
    to: routes.private.settings,
    label: <IconSettings />,
    isActive: (path: string) => !![routes.private.settingsRouted].find((match) => matchPath(match, path)),
  },
  {
    feature: FeatureKey.approvalRequests,
    to: routes.private.approvalRequests,
    label: i18n.t('common:navigation.approvalRequests'),
    isActive: (path: string) =>
      !![
        routes.private.approvalRequests,
        routes.private.approvalRequests,
        routes.private.approvalRequestsById,
        routes.private.approvalRequestsByIdAction,
        routes.private.approvalRequest,
        routes.private.approvalRequestEditById,
      ].find((match) => matchPath(match, path)),
  },
  {
    feature: FeatureKey.aiAssistant,
    to: routes.private.assistant,
    label: i18n.t('common:navigation.aiAssistant'),
    isActive: (path: string) => !!matchPath(`${routes.private.assistant}/*`, path),
  },
  {
    // A container entry: filterNavItems rewrites `to` to whichever tab
    // (Workflows / Custom lists) this viewer can actually open.
    feature: FeatureKey.workflows,
    to: routes.private.pipelines,
    label: i18n.t('common:navigation.workflows'),
    isActive: (path: string) =>
      !![
        routes.private.processes,
        routes.private.pipelineProcesses,
        routes.private.pipelines,
        routes.private.pipelineProcessesById,
        routes.private.pipelineProcesses,
        routes.private.pipelineApprovalRequest,
        routes.private.pipelineById,
        // Custom lists is a tab of this section, so the Workflows nav
        // entry stays highlighted while you're on it.
        routes.private.customLists,
        routes.private.customListById,
      ].find((match) => matchPath(match, path)),
  },
  {
    feature: FeatureKey.tasks,
    to: routes.private.tasks,
    label: i18n.t('common:navigation.tasks'),
    isActive: (path: string) => !!(matchPath(routes.private.tasks, path) || matchPath(routes.private.taskBoards, path)),
  },
  {
    feature: FeatureKey.reports,
    to: routes.private.reports,
    label: i18n.t('common:navigation.reports'),
    isActive: (path: string) =>
      !![routes.private.reports, routes.private.createReport, routes.private.reportById].find((match) =>
        matchPath(match, path),
      ),
  },
  {
    feature: FeatureKey.mailbox,
    to: routes.private.mailbox,
    label: i18n.t('common:navigation.mailbox'),
    isActive: (path: string) => !!matchPath(routes.private.mailbox, path),
  },
  {
    feature: FeatureKey.team,
    to: routes.private.organizationalStructure,
    label: i18n.t('common:navigation.team'),
    isActive: (path: string) =>
      !![
        routes.private.organizationalStructure,
        routes.private.teamDepartments,
        routes.private.teamDepartmentById,
        routes.private.teamLocations,
        routes.private.teamLocationById,
        routes.private.teamRoles,
      ].find((match) => matchPath(match, path)),
  },
  {
    // Workspace settings, mirror of the web AccountMenu entry.
    feature: FeatureKey.teamSettings,
    to: routes.private.teamSettings,
    label: i18n.t('common:navigation.teamSettings'),
    isActive: (path: string) =>
      !![routes.private.teamSettings, routes.private.teamSettingsRouted].find((match) => matchPath(match, path)),
  },
  {
    feature: FeatureKey.contracts,
    to: routes.private.contracts,
    label: i18n.t('common:navigation.contracts'),
    isActive: (path: string) => !!matchPath(routes.private.contracts, path),
  },
]

export const getSingleLinks = (user?: NavFilterUser | null): SingleLinkObject[] =>
  filterNavItems(SINGLE_LINKS, user ?? undefined, undefined, INCLUDE_LOCKED)

export const LinkGroupStylesConfig: StylesConfig<Option<string>, false> = {
  control: (base, props) => {
    return {
      ...base,
      cursor: 'pointer',
      backgroundColor: props.selectProps.value ? ' rgb(250, 245, 255,1)' : base.backgroundColor,
      borderRadius: 8,
      borderWidth: '2px',
      borderColor: 'rgb(229 231 235)',
      '&:hover': {
        borderColor: 'rgb(216 180 254 )',
      },
    }
  },
  dropdownIndicator: (base, props) => ({
    ...base,
    transform: props.selectProps.menuIsOpen ? 'rotate(180deg)' : '',
  }),
  placeholder: (base) => ({
    ...base,
    color: 'rgba(55, 65, 81, 1)',
    fontWeight: '500',
  }),
  singleValue: (base, props) => ({
    ...base,
    color: props.selectProps.value ? ' rgba(67, 56, 202, 1)' : 'black',
    fontWeight: '500',
  }),
  valueContainer: (base) => ({
    ...base,
    whiteSpace: 'nowrap',
  }),
}
